import { useCallback, useEffect, useRef, useState } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import PublicProgrammeBannerFrame from './home/PublicProgrammeBannerFrame';
import '../../styles/publicProgrammeCarousel.css';

export type ProgrammeItem = {
  id: string;
  icon: string;
  title: string;
  description: string;
  href?: string;
  linkLabel?: string;
};

type Props = {
  eyebrow: string;
  title: string;
  subtitle: string;
  bannerSrc: string;
  bannerAlt: string;
  items: ProgrammeItem[];
  exploreHref?: string;
  exploreLabel?: string;
};

export default function PublicProgrammeCarousel({
  eyebrow,
  title,
  subtitle,
  bannerSrc,
  bannerAlt,
  items,
  exploreHref = '#vacations',
  exploreLabel = 'Voir le planning des vacations',
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const syncNav = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollBack(el.scrollLeft > 4);
    setCanScrollForward(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    syncNav();
    const el = trackRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', syncNav, { passive: true });
    const ro = new ResizeObserver(syncNav);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', syncNav);
      ro.disconnect();
    };
  }, [syncNav, items.length]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    // Une « page » = tout le viewport du track (1 carte mobile, 3 cartes dès md)
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <div className="public-programme-section">
      <div className="public-programme-section__hero">
        <div className="public-programme-section__banner public-programme-section__banner--framed">
          <PublicProgrammeBannerFrame src={bannerSrc} alt={bannerAlt} />
        </div>

        <div className="public-programme-section__intro">
          <p className="public-programme-section__eyebrow text-xs font-bold uppercase tracking-widest text-on-surface-variant">
            {eyebrow}
          </p>
          <h3 className="public-programme-section__title mt-2 text-2xl font-black text-slate-900 sm:text-3xl">
            {title}
          </h3>
          <p className="public-programme-section__subtitle mt-3 text-sm leading-relaxed text-on-surface-variant sm:text-base">
            {subtitle}
          </p>
          {exploreHref ? (
            <a
              href={exploreHref}
              className="public-programme-section__explore public-programme-m365-card__link mt-4 inline-flex"
            >
              {exploreLabel}
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
            </a>
          ) : null}
        </div>
      </div>

      {items.length > 1 ? (
        <div className="mt-8 flex justify-end" aria-label="Navigation du carrousel programme">
          <div className="public-programme-carousel__nav shrink-0">
            <button
              type="button"
              className="public-programme-carousel__nav-btn"
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollBack}
              aria-label="Élément précédent"
            >
              <LucideSymbolIcon name="arrow_back" tone="inherit" size={20} />
            </button>
            <button
              type="button"
              className="public-programme-carousel__nav-btn"
              onClick={() => scrollByPage(1)}
              disabled={!canScrollForward}
              aria-label="Élément suivant"
            >
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={20} />
            </button>
          </div>
        </div>
      ) : null}

      <div className={`public-programme-carousel ${items.length > 1 ? 'mt-4' : 'mt-8'}`}>
        <div
          ref={trackRef}
          className="public-programme-carousel__track"
          role="list"
          aria-label="Éléments du programme de formation"
        >
          {items.map((item) => (
            <article key={item.id} className="public-programme-carousel__slide" role="listitem">
              <div className="public-programme-m365-card">
                <div className="public-programme-m365-card__icon" aria-hidden>
                  <LucideSymbolIcon name={item.icon} tone="inherit" size={24} />
                </div>
                <h4 className="public-programme-m365-card__title">{item.title}</h4>
                <p className="public-programme-m365-card__body">{item.description}</p>
                {item.href ? (
                  <a href={item.href} className="public-programme-m365-card__link">
                    {item.linkLabel ?? 'En savoir plus'}
                    <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
